import { Button, StatusMessage, Table } from '@driftt/dds'
import { forEachObjIndexed, isNil, merge, pathOr, toPairs } from 'ramda'
import React, { useEffect, useReducer, useState } from 'react'
import { useSelector } from 'react-redux'
import useSWR from 'swr'

import { getPlaybooksByGatedPlan } from 'api/playbooks'
import { getTasks } from 'api/tasks'
import {
  makePlaybooksTableColumns,
  PLAYBOOK_CLONE_TYPES,
  PLAYBOOK_INTERACTION_MODAL,
  TABLE_STATES,
} from 'components/PlaybooksTable/constants'
import { DEFAULT_API_PARAMS } from 'components/PlaybooksTable/index.js'
import tableEmptyState from 'images/table-empty-state.svg'
import { getOrgWorkspaces } from 'modules/workspace/selectors'
import { isEmptyOrNil } from 'utils'
import {
  getWorkspacesWithChatPlaybookRead,
  getWorkspacesWithFastlanePlaybookRead,
  hasGates,
  userHasAnyPlaybookAccess,
} from 'utils/access'

import './style.css'

const DeletePlaybookModal = React.lazy(() => import('../DeletePlaybookModal'))
const ClonePlaybookModal = React.lazy(() => import('../ClonePlaybookModal'))
const MovePlaybookModal = React.lazy(() => import('../MovePlaybookModal'))
const RestoreArchivedBlocksModal = React.lazy(() =>
  import('../RestoreArchivedBlocksModal')
)

const PlaybookInteractionModal = {
  [PLAYBOOK_INTERACTION_MODAL.DELETE]: DeletePlaybookModal,
  [PLAYBOOK_INTERACTION_MODAL.CLONE]: ClonePlaybookModal,
  [PLAYBOOK_INTERACTION_MODAL.MOVE]: MovePlaybookModal,
  [PLAYBOOK_INTERACTION_MODAL.CLONE_AS_BOT]: ClonePlaybookModal,
  [PLAYBOOK_INTERACTION_MODAL.CLONE_AS_CLP]: ClonePlaybookModal,
  [PLAYBOOK_INTERACTION_MODAL.CLONE_AS_BLOCK]: ClonePlaybookModal,
  [PLAYBOOK_INTERACTION_MODAL.RESTORE_ARCHIVED_BLOCKS]:
    RestoreArchivedBlocksModal,
}

const keyToCloneType = {
  [PLAYBOOK_INTERACTION_MODAL.CLONE_AS_BOT]: PLAYBOOK_CLONE_TYPES.BOT,
  [PLAYBOOK_INTERACTION_MODAL.CLONE_AS_CLP]: PLAYBOOK_CLONE_TYPES.CLP,
  [PLAYBOOK_INTERACTION_MODAL.CLONE_AS_BLOCK]: PLAYBOOK_CLONE_TYPES.BLOCK,
}

const TABLE_EVENTS = Object.freeze({
  PAGINATION: 'pagination',
  SORTING: 'sorting',
})

export default function PlaybooksTable({
  apiParams,
  playbooksSWR,
  onChange,
  orgUsers,
  showTransientNotification,
  playbookType,
  isPrioritizedPlaybooksOpen,
  history,
}) {
  const { data, isValidating, error, mutate } = playbooksSWR
  const hasNameKeyword =
    !isNil(apiParams.nameQuery) && apiParams.nameQuery?.length > 0
  const hasAnyPlaybookAccess = userHasAnyPlaybookAccess()
  let emptyState = null

  if (!hasNameKeyword && playbookType === 'FASTLANE') {
    // No playbooks or sequences created yet
    emptyState = {
      image: <img src={tableEmptyState} alt="Empty table" />,
      title:
        'Let qualified, high-intent leads skip the queue and book a meeting \n immediately after submitting a form!',
      subtitle: ' ',
      action: (
        <Button
          variant={'secondary'}
          onClick={() => {
            history.push('/list/fastlane/create')
          }}
        >
          Create Fastlane playbook
        </Button>
      ),
    }
  } else {
    // Has playbooks and a search query exist.
    emptyState = {
      title: `There are no ${
        hasAnyPlaybookAccess ? 'playbook' : 'sequence'
      } that match this search`,
      subtitle: `Click on Create ${
        hasAnyPlaybookAccess ? 'playbook' : 'sequence'
      } to get started!`,
    }
  }

  const [enabledPlaybookReportTypes, setEnabledPlaybookReportTypes] = useState(
    {}
  )

  const [playbookRowModalContext, setPlaybookRowModalContext] = useReducer(
    merge,
    {
      [PLAYBOOK_INTERACTION_MODAL.DELETE]: null,
      [PLAYBOOK_INTERACTION_MODAL.CLONE]: null,
      [PLAYBOOK_INTERACTION_MODAL.MOVE]: null,
    }
  )

  const orgWorkspaces = useSelector(getOrgWorkspaces)
  const showWorkspaces =
    (playbookType === 'FASTLANE'
      ? getWorkspacesWithFastlanePlaybookRead()
      : getWorkspacesWithChatPlaybookRead()
    ).length > 1

  const size = apiParams.pageSize || 10
  const tableState = pathOr(TABLE_STATES.CHAT_ACTIVE, ['category'], apiParams)

  const isDataLoading = isEmptyOrNil(data?.playbooks) && isValidating

  const { data: tasksByPlaybook } = useSWR(
    playbooksSWR?.data?.playbooks.map(({ playbook }) => playbook.id) ?? null,
    async (...playbookIds) => await getTasks(playbookIds),
    {
      dedupingInterval: 300000,
    }
  )

  useEffect(() => {
    async function getPlaybooksByGatedPlanEffect() {
      try {
        const playbookReportTypePlansEnabled = {}
        const { data } = await getPlaybooksByGatedPlan()

        function checkIfEnabledPlaybook(gates, playbookType) {
          playbookReportTypePlansEnabled[playbookType] =
            gates.length === 0 || gates.some((gate) => hasGates(gate))
        }

        forEachObjIndexed(checkIfEnabledPlaybook, data)
        setEnabledPlaybookReportTypes(playbookReportTypePlansEnabled)
      } catch (err) {
        console.warn(err)
      }
    }

    getPlaybooksByGatedPlanEffect()
  }, [])

  const getPlaybookRow = () => {
    if (!data) {
      return []
    }

    return data?.playbooks?.map(({ playbook }) => {
      const taskData =
        !isEmptyOrNil(tasksByPlaybook) && tasksByPlaybook?.[playbook?.id]?.[0]

      return {
        ...playbook,
        ...(!isEmptyOrNil(taskData) && !!taskData && taskData),
      }
    })
  }

  const getFormattedColumns = () =>
    makePlaybooksTableColumns({
      tableState,
      orgUsers,
      showWorkspaces,
      workspaces: orgWorkspaces,
      onChange: playbooksSWR.mutate,
      enabledPlaybookReportTypes,
      setPlaybookRowModalContext,
      showTransientNotification,
    })

  if (!isEmptyOrNil(error)) {
    return (
      <StatusMessage variant="error">
        We are unable to fetch your playbooks at this time
      </StatusMessage>
    )
  }

  return (
    <>
      {!isPrioritizedPlaybooksOpen && (
        <Table
          emptyState={emptyState}
          isLoading={isDataLoading}
          disableBoxShadow
          columns={getFormattedColumns()}
          rows={getPlaybookRow()}
          pagination={{
            defaultPagination: {
              pageIndex: apiParams.page,
              pageSize: size,
            },
            rowTotal: data?.meta?.total ?? 0,
          }}
          onStateChange={({ event, pagination, sorting }) => {
            if (event === TABLE_EVENTS.PAGINATION) {
              onChange({
                page: pagination.pageIndex,
                pageSize: pagination.pageSize,
              })
            }
            if (event === TABLE_EVENTS.SORTING) {
              const newSort = sorting?.[sorting?.length - 1]
              const sortProperties = newSort
                ? {
                    sortProperty: newSort.id,
                    sortDirection: newSort.desc ? 'desc' : 'asc',
                  }
                : {
                    sortProperty: DEFAULT_API_PARAMS.sortProperty,
                    sortDirection: DEFAULT_API_PARAMS.sortDirection,
                  }

              onChange(sortProperties)
            }
          }}
        />
      )}

      {toPairs(PlaybookInteractionModal).map(([key, ModalComponent]) => (
        <React.Suspense fallback={<div />} key={key}>
          <ModalComponent
            open={!!playbookRowModalContext[key]}
            onClose={() => setPlaybookRowModalContext({ [key]: null })}
            onConfirm={mutate}
            cloneType={keyToCloneType[key]}
            {...playbookRowModalContext[key]}
          />
        </React.Suspense>
      ))}
    </>
  )
}
